import { motion } from "framer-motion";
import { colors, fonts, gradients, animations } from "@/lib/theme";

interface ExhibitHeaderProps {
  title?: string | null;
  description?: string | null;
}

/**
 * Full-viewport exhibit header with tinted background
 * Displays exhibit title and description, or a subtle divider if empty
 */
export function ExhibitHeader({ title, description }: ExhibitHeaderProps) {
  const hasContent = title || description;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: false, amount: 0.3 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="min-h-screen flex items-center justify-center mb-16 -mx-6 px-6"
      style={{ background: gradients.exhibitHeader }}
    >
      <motion.div
        {...animations.headerFadeIn}
        whileInView={animations.headerFadeIn.animate}
        viewport={{ once: false, amount: 0.5 }}
        className="text-center max-w-2xl"
      >
        {title && (
          <h2
            className="text-3xl sm:text-4xl md:text-5xl mb-4"
            style={{
              fontFamily: fonts.serif,
              color: colors.text,
              fontWeight: "normal",
              letterSpacing: "-0.02em",
            }}
          >
            {title}
          </h2>
        )}

        {description && (
          <p
            className="text-base md:text-lg"
            style={{
              fontFamily: fonts.sans,
              color: colors.textMuted,
              lineHeight: "1.7",
            }}
          >
            {description}
          </p>
        )}

        {/* Subtle divider when no content */}
        {!hasContent && (
          <div
            style={{
              width: "60px",
              height: "2px",
              backgroundColor: colors.primaryMuted,
              margin: "0 auto",
            }}
          />
        )}
      </motion.div>
    </motion.div>
  );
}
